import { Bookmark, BookmarkCheckFill } from 'react-bootstrap-icons';
import { useParams } from 'react-router-dom';

import { Skeleton, Typography, Button } from 'antd';

import { IModule } from '@/interfaces/course';
import { useGetLessonSavedQuery, useSaveLessonMutation } from '@/services/savedLessons';
import styled, { StyledProp, cx } from '@/styled';

interface IHeaderProps {
  module: IModule;
  lessonTitle?: string;
  watched?: boolean;
}

export const Header = ({ className, lessonTitle, module }: StyledProp & IHeaderProps) => {
  const { id } = useParams();
  const [saveLesson, { isLoading }] = useSaveLessonMutation();
  const { isSaved } = useGetLessonSavedQuery(parseInt(id), {
    selectFromResult: ({ data }) => {
      return { isSaved: data };
    }
  });

  if (!lessonTitle) {
    return (
      <div className={cx(className, 'headerSkeleton')}>
        <Skeleton.Input block className='skeleton' size='small' />
        <Skeleton.Input block className='skeleton' size='large' />
      </div>
    );
  }

  const handleSaveLesson = () => {
    if (id) {
      saveLesson(parseInt(id));
    }
  };

  return (
    <div className={className}>
      <Typography.Title level={4} className='subtitle'>
        {module?.title}
      </Typography.Title>
      <div className='buttonTextContainer'>
        <Typography.Title level={1} className='title'>
          {lessonTitle}
        </Typography.Title>
        {/* <Button onClick={handleDownload} shape='circle' className='button' icon={<Download size={20} />} /> */}
        <Button
          className='button'
          icon={isSaved ? <BookmarkCheckFill size={20} /> : <Bookmark size={20} />}
          onClick={handleSaveLesson}
          loading={isLoading}
          shape='circle'
        />
      </div>
    </div>
  );
};

export default styled(Header)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-direction: column;

  .title {
    &.ant-typography {
      margin: 0;
    }
  }

  &.headerSkeleton {
    gap: ${({ theme }) => theme.space.spacing(4)};
    display: flex;
    flex-direction: column;
    margin-bottom: ${({ theme }) => theme.space.spacing(4)};
  }

  .subtitle {
    margin: 0;
    color: ${({ theme }) => theme.colorTextTertiary};
    font-weight: 400;
  }

  .subtitleContainer {
    width: 100%;
    display: flex;
    max-width: 900px;
    flex-direction: column;
    margin-bottom: ${({ theme }) => theme.space.spacing(2)};
  }

  .buttonTextContainer {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;

    .button {
      background: ${({ theme }) => theme.variables.contentButtonSaveLesson.background};
      height: 40px;
      border: 1px solid ${({ theme }) => theme.variables.contentButtonSaveLesson.border};
      display: flex;
      width: 40px;
      min-width: 40px;
      transition: ${({ theme }) => theme.motionDurationMid};

      &:hover {
        background: ${({ theme }) => theme.variables.contentButtonSaveLesson.hover.background};

        svg {
          color: ${({ theme }) => theme.variables.contentButtonSaveLesson.hover.color};
        }
      }

      svg,
      span {
        transition: ${({ theme }) => theme.motionDurationMid};
        color: ${({ theme }) => theme.variables.contentButtonSaveLesson.color};
        margin: auto;
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('lg')} {
    .title {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      font-size: ${({ theme }) => theme.space.pxToRem(24)};
    }

    .subtitle {
      font-size: ${({ theme }) => theme.space.pxToRem(16)};
    }

    padding: ${({ theme }) => theme.space.spacing(0, 4)};
  }
`;
